import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  UserCircle,
  Tag,
  TrendingUp,
  MessageSquare,
  LogOut,
  Menu,
  X,
  Bot,
  Settings,
  Package,
  BarChart3,
  Activity,
  Send,
  Smartphone,
  Upload,
  ShieldAlert,
  Brain,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NotificationBell } from '@/components/NotificationBell';
import { useState } from 'react';

export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isSupervisor = user?.role === 'supervisor';
  const isPrivileged = isAdmin || isSupervisor;

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, access: 'all' },
    { path: '/leads', label: 'Leads', icon: Users, access: 'all' },
    { path: '/operators', label: 'Operadores', icon: UserCircle, access: 'privileged' },
    { path: '/tags', label: 'Etiquetas', icon: Tag, access: 'all' },
    { path: '/sales', label: 'Vendas', icon: TrendingUp, access: 'all' },
    { path: '/products', label: 'Produtos', icon: Package, access: 'privileged' },
    { path: '/whatsapp', label: 'WhatsApp', icon: MessageSquare, access: 'all' },
    { path: '/broadcast', label: 'Disparo em Massa', icon: Send, access: 'privileged' },
    { path: '/import', label: 'Importar Base', icon: Upload, access: 'privileged' },
    { path: '/instances', label: 'Instâncias', icon: Smartphone, access: 'privileged' },
    { path: '/whatsapp-config', label: 'Config WhatsApp', icon: Settings, access: 'privileged' },
    { path: '/performance', label: 'Performance', icon: BarChart3, access: 'privileged' },
    { path: '/webhook-logs', label: 'Webhook Logs', icon: Activity, access: 'admin' },
    { path: '/whatsapp-recovery', label: 'Recuperação WhatsApp', icon: ShieldAlert, access: 'admin' },
    { path: '/reengagement', label: 'IA Reengajamento', icon: Brain, access: 'privileged' },
    { path: '/ai-assistant', label: 'IA Sofia', icon: Bot, access: 'all' },
  ];

  const filteredItems = menuItems.filter(item => {
    if (item.access === 'all') return true;
    if (item.access === 'admin') return isAdmin;
    if (item.access === 'privileged') return isPrivileged;
    return false;
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const SidebarContent = () => (
    <>
      <div className="p-3 border-b border-teal-800/30 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-white border border-emerald-100 flex items-center justify-center p-1 shrink-0">
              <img
                src="https://customer-assets.emergentagent.com/job_bonavita-novo/artifacts/77x2xbm3_logo_bio-top-suplementos-naturais_M0lZxH.png"
                alt="Biotop"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Biotop
              </h1>
              <p className="text-[11px] text-slate-500 leading-tight truncate">{user?.name}</p>
              <p className="text-[10px] text-slate-400 leading-tight">{user?.role === 'admin' ? 'Administrador' : user?.role === 'supervisor' ? 'Supervisor' : 'Operador'}</p>
            </div>
          </div>
          <NotificationBell />
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5 min-h-0">
        {filteredItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                isActive
                  ? 'bg-emerald-50 text-emerald-700 font-medium'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
              onClick={() => setIsOpen(false)}
            >
              <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
              <span className="truncate">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-3 border-t border-slate-200/50 shrink-0">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50 text-sm"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Sair
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile menu button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-md"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="mobile-menu-button"
      >
        {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-60 bg-white/80 backdrop-blur-md border-r border-slate-200/50 h-screen sticky top-0">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {isOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsOpen(false)}
          />
          <aside className="lg:hidden fixed left-0 top-0 bottom-0 w-60 bg-white z-50 flex flex-col">
            <SidebarContent />
          </aside>
        </>
      )}
    </>
  );
};
